import React, { useEffect, useState } from 'react';
import { PubSub } from '@instinet-analytics/core-lib';
import classNames from 'classnames';
import './FloatingLegend.scss';
import { OrderSide } from 'communication/types/order/OrderSide';

export interface MouseOverState {
    y?: number;
}

export type MouseOverPubSub<T extends MouseOverState> = PubSub<T>;

export interface FloatingLegendProps<T extends MouseOverState, TLegendData> {
    chartHeight: number;
    mouseOver: MouseOverPubSub<T>;
    className: string;
    deriveLegendData: (mouseOverState: T) => TLegendData | undefined;
    deriveLegendClasses?: (legendData: TLegendData) => string;
    renderLegends: (legendData: TLegendData) => React.ReactNode;
}

export function FloatingLegend<T extends MouseOverState, TLegendData>(
    props: FloatingLegendProps<T, TLegendData>
) {
    const {
        className,
        chartHeight,
        mouseOver,
        deriveLegendData,
        deriveLegendClasses,
        renderLegends,
    } = props;

    type PositionClassValues = 'top' | 'bottom';
    const [positionClass, setPositionClass] =
        useState<PositionClassValues>('top');
    const [legendData, setLegendData] = useState<TLegendData | undefined>();

    useEffect(() => {
        mouseOver.subscribe('FloatingLegend', (mouseOverState) => {
            setPositionClass(
                (mouseOverState?.y ?? 0) > chartHeight / 2 ? 'top' : 'bottom'
            );
            setLegendData(
                mouseOverState ? deriveLegendData(mouseOverState) : undefined
            );
        });

        return () => {
            mouseOver.unsubscribe('FloatingLegend');
        };
    });

    if (!legendData) {
        return <></>;
    }

    const cssClasses = classNames(
        'FloatingLegend',
        'Legend',
        className,
        positionClass,
        deriveLegendClasses && deriveLegendClasses(legendData)
    );

    const legends = renderLegends(legendData);

    if (!legends) {
        return <></>;
    }
    return <div className={cssClasses}>{legends}</div>;
}

function renderRow(title: string, value?: string, suffix?: string) {
    return (
        <div className="row">
            <div className="label">{title}</div>
            <div className="value">{value}</div>
            {suffix && <div className="suffix">{suffix}</div>}
        </div>
    );
}
FloatingLegend.renderRow = renderRow;

function toSideClass(side: OrderSide) {
    return side === 'B'
        ? 'buy'
        : side === 'S'
        ? 'sell'
        : side === 'SS'
        ? 'short'
        : '';
}
FloatingLegend.toSideClass = toSideClass;

FloatingLegend.WellKnownCssClasses = {
    buy: 'side-B',
    sell: 'side-S',
    short: 'side-SS',
};
